import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'

import InformationContent from '@components/UI/Information/Content/Content'
import UpsellInformation from '@components/Upsell/Information/Information'
import { CPM_EXPLAINED } from '@constants'
import { EventTrackingNames } from '@enums'
import { IApplicationState } from '@store'
import { selectCampaignOperatingRegionTaxName } from '@store/campaigns'
import { selectCampaignId, selectSubmissionId } from '@store/router'
import Theme from '@theme'
import { Container, Link, Text } from '@tribegroup'

interface IProps {
  title: string
  locked?: boolean
  currency?: string
  externalLink?: string
  productMetricValue?: string
  productMetricGroup?: string
  translationPrefix?: string
  showAsterisk?: boolean
}

const AnalyticsPostCampaignReportMetricTooltip: React.FC<IProps> = ({
  locked,
  title,
  externalLink,
  currency,
  productMetricValue,
  productMetricGroup,
  showAsterisk,
  translationPrefix = 'postCampaignReport.performance.item.label.text',
}) => {
  const match = useRouteMatch()
  const intl = useIntl()
  const campaignId = selectCampaignId(match)!
  const submissionId = selectSubmissionId(match)!
  const taxName = useSelector((state: IApplicationState) =>
    selectCampaignOperatingRegionTaxName(state, campaignId, intl, currency),
  )

  const metricMessageId = `${translationPrefix}.${title}`

  return (
    <>
      <InformationContent
        titleColor={locked ? Theme.grey900Color : Theme.primaryColor}
        title={<FormattedMessage id={metricMessageId} />}
        content={
          <FormattedMessage
            id={`${metricMessageId}.tooltip`}
            values={{
              taxDisplay: taxName,
              link: (
                <Link href={CPM_EXPLAINED} target="_blank" color={Theme.primaryColor} lowercase>
                  <FormattedMessage id="core.text.clickHere" />
                </Link>
              ),
            }}
          />
        }
      />

      {showAsterisk && (
        <Container topOuterSpacing={0.5}>
          <Container absolute leftPosition={0.75}>
            <Text medium color={Theme.grey900Color} customFontSize={0.8125}>
              <FormattedMessage id="core.text.symbol.attention" />
            </Text>
          </Container>
          <Text color={Theme.grey900Color} customFontSize={0.8125}>
            <FormattedMessage id="postCampaignReport.performance.tooltipDisclaimer" />
          </Text>
        </Container>
      )}

      {locked && externalLink && (
        <UpsellInformation
          textInformationMaxWidth={9.75}
          textInformation={
            <FormattedMessage id="upsell.information.unlock" values={{ br: () => <br /> }} />
          }
          externalLink={externalLink}
          trackingProps={{
            eventName: EventTrackingNames.SubscriptionLearnMore,
            additionalProperties: {
              current_filter: 'metrics',
              product_metric_group: productMetricGroup,
              product_metric: productMetricValue,
            },
            submissionId,
            campaignId,
          }}
        />
      )}
    </>
  )
}

export { AnalyticsPostCampaignReportMetricTooltip }
export default AnalyticsPostCampaignReportMetricTooltip
